import React, { Suspense, lazy } from 'react';
import PageLoader from '../components/PageLoader';
import AuthRoute from '../components/AuthRoute';
import ProtectedRoute from '../components/ProtectedRoute';
import { SubviewsOrchestratorProvider } from "../context/SubviewsOrchestratorProvider";
import {
    Input as InputIcon,
    Output as OutputIcon,
    Warehouse as WarehouseIcon,
    FactCheck as FactCheckIcon,
    Storefront as StorefrontIcon,
} from "@mui/icons-material";
import ProductsLocationContextProvider from "../pages/Ubicazione/context/ProductsLocationContextProvider.jsx";

// Lazy loading dei componenti
const Login = lazy(() => import('../pages/LoginPage'));
const Home = lazy(() => import('../pages/Home'));
const Ops = lazy(() => import('../pages/Ops'));
const CarrierSelection = lazy(() => import('../pages/Prelievo/CarrierSelection'));
const Ricerca = lazy(() => import('../pages/Ricerca/Ricerca'));
const Prelievo = lazy(() => import('../pages/Prelievo/Index'));
const PrelieviAvviati = lazy(() => import('../pages/Prelievo/PrelieviAvviati'));
const SelezioneDepositi = lazy(() => import('../pages/Ubicazione/SelezioneDepositi/Index'));
const UbicazioneProdotti = React.lazy(() => import('../pages/Ubicazione/UbicazioneProdotti/Index'));
const UbicazioniStock = lazy(() => import('../pages/UbicazioniStock/Index'));
const RiempimentoDaStock = lazy(() => import('../pages/RiempimentoDaStock/Index'));
const CaricoDaPalmare = lazy(() => import('../pages/CaricoDaPalmare/Index'));
const CaricoProdotto = lazy(() => import('../pages/CaricoProdotto/Index'));
const VerificaProdotti = lazy(() => import('../pages/VerificaProdotti/Index'));
const AdminSessions = lazy(() => import('../pages/AdminSessions'));
const OrdineNegozio = lazy(() => import('../pages/OrdineNegozio/Index'));

const WithSuspense = ({Component}) => {
    return (
        <Suspense fallback={<PageLoader/>}>
            <Component/>
        </Suspense>
    )
}

const OpsComponent = () => {
    return (
        <ProtectedRoute>
            <WithSuspense Component={() => <Ops/>}/>
        </ProtectedRoute>
    )
}

// Definizione delle routes
export const ROUTES = {
    LOGIN: {
        path: '/login',
        key: 'login',
        title: 'Login',
        render: () => (
            <AuthRoute>
                <WithSuspense Component={Login}/>
            </AuthRoute>
        )
    },
    HOME: {
        path: '/',
        key: 'home',
        title: 'Home',
        render: () => (
            <ProtectedRoute>
                <WithSuspense Component={Home}/>
            </ProtectedRoute>
        )
    },
    OPERATIONS: {
        INGRESSO: {
            path: '/ops/:key',
            key: 'ingresso',
            title: 'Ingresso Merce',
            render: () => (
                <OpsComponent/>
            )
        },
        USCITA: {
            path: '/ops/:key',
            key: 'uscita',
            title: 'Uscita Merce',
            render: () => (
                <OpsComponent/>
            )
        },
        ALTRE: {
            path: '/ops/:key',
            key: 'altre',
            title: 'Altre Movimentazioni',
            render: () => (
                <OpsComponent/>
            )
        },
        NEGOZIO: {
            path: '/ops/:key',
            key: 'negozio',
            title: 'Negozio',
            render: () => (
                <OpsComponent/>
            )
        }
    },
    CARRIER_SELECTION: {
        path: '/ops/uscita/carrier-selection',
        key: 'carrier-selection',
        title: 'Selezione Carriere',
        render: () => (
            <ProtectedRoute>
                <WithSuspense Component={CarrierSelection}/>
            </ProtectedRoute>
        )
    },
    PICKING: {
        path: '/ops/uscita/pickings',
        key: 'pickings',
        title: 'Prelievi',
        render: () => (
            <ProtectedRoute>
                <WithSuspense Component={Prelievo}/>
            </ProtectedRoute>
        )
    },
    PICKING_STARTED: {
        path: '/ops/uscita/pickings/user',
        key: 'pickings_started',
        title: 'Prelievi Avviati',
        render: () => (
            <ProtectedRoute>
                <WithSuspense Component={PrelieviAvviati}/>
            </ProtectedRoute>
        )
    },
    RICERCA: {
        path: '/search',
        key: 'search',
        title: 'Ricerca',
        render: () => (
            <ProtectedRoute>
                <SubviewsOrchestratorProvider>
                    <WithSuspense Component={Ricerca}/>
                </SubviewsOrchestratorProvider>
            </ProtectedRoute>
        )
    },
    UBICATIONI_STOCK: {
        path: '/ops/altre/stock_locations',
        key: 'stock_locations',
        title: 'Ubicazioni Stock',
        render: () => (
            <ProtectedRoute>
                <WithSuspense Component={UbicazioniStock}/>
            </ProtectedRoute>
        )
    },
    RIEMPIMENTO_DA_STOCK: {
        path: '/ops/altre/refill_from_stock/:id',
        key: 'refill_from_stock',
        title: 'Riempimento da Stock',
        render: () => (
            <ProtectedRoute>
                <WithSuspense Component={RiempimentoDaStock}/>
            </ProtectedRoute>
        )
    },
    SELEZIONE_DEPOSITI: {
        path: '/ops/ingresso/depot_selection',
        key: 'selezione_depositi',
        title: 'Selezione Depositi',
        render: () => (
            <ProtectedRoute>
                <ProductsLocationContextProvider>
                    <WithSuspense Component={SelezioneDepositi}/>
                </ProductsLocationContextProvider>
            </ProtectedRoute>
        )
    },
    UBICAZIONE_PRODOTTI: {
        path: '/ops/ingresso/locations/products',
        key: 'ubicazione_prodotti',
        title: 'Ubicazione Prodotti',
        render: () => (
            <ProtectedRoute>
                <ProductsLocationContextProvider>
                    <SubviewsOrchestratorProvider>
                        <WithSuspense Component={UbicazioneProdotti}/>
                    </SubviewsOrchestratorProvider>
                </ProductsLocationContextProvider>
            </ProtectedRoute>
        )
    },
    CARICO_DA_PALMARE: {
        path: '/ops/ingresso/carico-da-palmare',
        key: 'carico_da_palmare',
        title: 'Carico Merce',
        render: () => (
            <ProtectedRoute>
                <WithSuspense Component={CaricoDaPalmare}/>
            </ProtectedRoute>
        )
    },
    CARICO_PRODOTTO: {
        path: '/ops/ingresso/carico-prodotto',
        key: 'carico_prodotto',
        title: 'Carico Prodotto',
        render: () => (
            <ProtectedRoute>
                <WithSuspense Component={CaricoProdotto}/>
            </ProtectedRoute>
        )
    },
    VERIFICA_PRODOTTI: {
        path: '/ops/ingresso/verifica-prodotti',
        key: 'verifica_prodotti',
        title: 'Verifica Prodotti',
        render: () => (
            <ProtectedRoute>
                <WithSuspense Component={VerificaProdotti}/>
            </ProtectedRoute>
        )
    },
    ADMIN_SESSIONS: {
        path: '/admin/sessions',
        key: 'admin_sessions',
        title: 'Gestione Sessioni',
        render: () => (
            <ProtectedRoute>
                <WithSuspense Component={AdminSessions}/>
            </ProtectedRoute>
        )
    },
    ORDINE_NEGOZIO: {
        path: '/ops/negozio/ordini',
        key: 'ordine_negozio',
        title: 'Ordine Negozio',
        render: () => (
            <ProtectedRoute>
                <WithSuspense Component={OrdineNegozio}/>
            </ProtectedRoute>
        )
    },
};

export const OPS_MAPPING = [
    {
        id: 1,
        title: 'Ingresso Merce',
        icon: <InputIcon sx={{ fontSize: 60 }} />,
        route: ROUTES.OPERATIONS.INGRESSO,
        sections: [
            {
                id: 1,
                title: ROUTES.CARICO_DA_PALMARE.title,
                icon: <InputIcon sx={{ fontSize: 60 }} />,
                route: ROUTES.CARICO_DA_PALMARE,
            },
            {
                id: 2,
                title: ROUTES.SELEZIONE_DEPOSITI.title,
                icon: <InputIcon sx={{ fontSize: 60 }} />,
                route: ROUTES.SELEZIONE_DEPOSITI,
            },
        ],
    },
    {
        id: 2,
        title: 'Uscita Merce',
        icon: <OutputIcon sx={{ fontSize: 60 }} />,
        route: ROUTES.OPERATIONS.USCITA,
        sections: [
            {
                id: 1,
                title: 'Prelievo',
                icon: <InputIcon sx={{ fontSize: 60 }} />,
                route: ROUTES.CARRIER_SELECTION,
            },
        ],
    },
    {
        id: 3,
        title: 'Altre Movimentazioni',
        icon: <WarehouseIcon sx={{ fontSize: 60 }} />,
        route: ROUTES.OPERATIONS.ALTRE,
        sections: [
            {
                id: 1,
                title: 'Ubicazioni Stock',
                icon: <InputIcon sx={{ fontSize: 60 }} />,
                route: ROUTES.UBICATIONI_STOCK,
            },
            {
                id: 2,
                title: 'Verifica Prodotti',
                icon: <FactCheckIcon sx={{ fontSize: 60 }} />,
                route: ROUTES.VERIFICA_PRODOTTI,
            },
        ],
    },
    {
        id: 4,
        title: 'Negozio',
        icon: <StorefrontIcon sx={{ fontSize: 60 }} />,
        route: ROUTES.OPERATIONS.NEGOZIO,
        sections: [
            {
                id: 1,
                title: ROUTES.ORDINE_NEGOZIO.title,
                icon: <StorefrontIcon sx={{ fontSize: 60 }} />,
                route: ROUTES.ORDINE_NEGOZIO,
            },
        ],
    },
];

// Helper per ottenere un array di rotte piatte per il routing
export const getRoutesArray = () => {
    const result = [];

    // Funzione ricorsiva per appiattire la struttura
    const flattenRoutes = (routesObj, result) => {
        Object.values(routesObj).forEach(route => {
            if (route.path) {
                // Se ha un path, è una rotta
                result.push(route);
            } else {
                // Altrimenti è un gruppo di rotte
                flattenRoutes(route, result);
            }
        });
    };

    flattenRoutes(ROUTES, result);
    return result;
};

// Per compatibilità con il codice esistente
export const getRouteByKey = (key) => {
    return getRoutesArray().find(route => route.key === key);
};

export const getPathByKey = (key) => {
    const route = getRouteByKey(key);
    return route ? route.path : null;
};